import React, { Suspense, useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { CommentWithReplyField, FloatingContainer } from '@contently/toolkit';
import { useResource, useFetcher, useInvalidator } from 'rest-hooks';
import AnnotationResource from '../../resources/AnnotationResource';
import useOutsideClick from '../../hooks/useOutsideClick';
import CommentResource from '../../resources/CommentResource';
import useInvalidateCacheOnUnmount from '../../hooks/useInvalidateCacheOnUnmount';
import useUserContext from '../../hooks/useUserContext';
import useStoryContext from '../../hooks/useStoryContext';
import authorOwnerCheck from '../../utils/authorOwnerCheck';
import ReplyChainContainer from './ReplyChainContainer';
import LoadingSpinner from '../../../utils/shared/LoadingSpinner';
import { getReplyCommentParams, getAnnotationParams } from './utils';
import {
  updateResolvedAnnotation,
  updateAnnotationListCacheBust,
  updateCommentListCacheBust
} from '../../store/textEditorReducer';
import useAvatarBadge from '../../hooks/useAvatarBadge';

export default function TextEditorAnnotationComment({
  activeAnnotation,
  allContentIds,
  yPos,
  hide,
  storyId,
  filterState
}) {
  const { comment } = useResource(AnnotationResource.detailShape(), {
    markId: activeAnnotation
  });
  const { mentionableUsersBasicInfo } = useStoryContext();
  const { permissions } = useUserContext();
  const createComment = useFetcher(CommentResource.createShape());
  useInvalidateCacheOnUnmount(AnnotationResource.detailShape(), {
    markId: activeAnnotation
  });
  const editComment = useFetcher(CommentResource.updateShape());
  const dispatch = useDispatch();

  const invalidateAnnotationList = useInvalidator(AnnotationResource.listShape());
  const activeAnnotationRef = useRef(null);
  useOutsideClick(activeAnnotationRef, hide);
  const [commentBody, setCommentBody] = useState(comment.body);

  // State to track if any replies mention the current user
  const [hasReplyMentions, setHasReplyMentions] = useState(false);

  // Get current user for mention detection
  const currentUser =
    mentionableUsersBasicInfo && mentionableUsersBasicInfo.find(user => user.id === permissions.id);

  // Check if the comment body mentions the current user
  const commentMentionsUser =
    currentUser &&
    comment.body &&
    (comment.body.includes(`**${currentUser.display}**`) ||
      comment.body.includes(currentUser.display));

  // Convert comment.id to number if it's a string
  const commentId = comment.id ? Number(comment.id) : null;

  // If we're in "Mentions Me" filter and neither the comment nor any replies mention the user,
  // don't render this component
  if (
    filterState === 'Mentions Me' &&
    ((!commentMentionsUser && !hasReplyMentions) || comment.resolved)
  ) {
    return null;
  }

  if (!commentId) return null;

  const [avatarTooltip, avatarBadge, avatarBadgeColor] = useAvatarBadge(
    comment.authorable.fullName,
    comment.authorIsEmployee,
    comment.authorIsManagingEditor
  );

  const handleEdit = data => {
    setCommentBody(data.edit);
    editComment({ id: commentId }, { body: data.edit });
    invalidateAnnotationList(getAnnotationParams(allContentIds));
    dispatch(updateAnnotationListCacheBust(true));
  };

  const handleReply = data => {
    createComment({}, getReplyCommentParams(data, storyId, commentId), [
      [
        CommentResource.listShape(),
        { parent_id: commentId },
        (newCommentId, commentIds) => {
          return [...(commentIds || []), newCommentId];
        }
      ]
    ]);
    dispatch(updateCommentListCacheBust(commentId));
  };

  const handleCancel = e => {
    e.preventDefault();
    hide();
  };

  const handleResolve = id => {
    dispatch(updateResolvedAnnotation(activeAnnotation));
    editComment({ id: id }, { resolved: 1 });
    invalidateAnnotationList(getAnnotationParams(allContentIds));
    dispatch(updateAnnotationListCacheBust(true));
  };

  useEffect(() => {
    console.log(`Annotation ${activeAnnotation} - Reply mentions: ${hasReplyMentions}`);
  }, [hasReplyMentions, activeAnnotation]);

  return (
    <div ref={activeAnnotationRef}>
      <FloatingContainer right={-420} top={yPos} resolved={!!comment.resolved}>
        <CommentWithReplyField
          id={commentId}
          body={commentBody}
          avatar={comment.authorable.avatar}
          authorIsOwner={authorOwnerCheck(comment.authorable.id, permissions.id)}
          fullName={comment.authorable.fullName}
          formattedCreatedAt={comment.formattedCreatedAt}
          authorIsEmployee={comment.authorIsEmployee}
          authorIsManagingEditor={comment.authorIsManagingEditor}
          onResolve={() => handleResolve(commentId)}
          resolved={comment.resolved}
          onEditSubmit={e => handleEdit(e)}
          onReplySubmit={e => handleReply(e)}
          onReplyCancel={e => handleCancel(e)}
          borderSides={false}
          showReplyForm={!comment.resolved}
          users={mentionableUsersBasicInfo}
          avatarTooltip={avatarTooltip}
          authorBadge={avatarBadge}
          authorBadgeColor={avatarBadgeColor}
          borderLeft
          highlightMention={commentMentionsUser && filterState === 'Mentions Me'}
        >
          <Suspense fallback={<LoadingSpinner />}>
            <ReplyChainContainer
              commentId={commentId}
              showAllComments={true}
              enableEdit={true}
              users={mentionableUsersBasicInfo}
              sidebar={false}
              filterState={filterState}
              onMentionsFound={setHasReplyMentions}
            />
          </Suspense>
        </CommentWithReplyField>
      </FloatingContainer>
    </div>
  );
}

TextEditorAnnotationComment.defaultProps = {
  hide: null,
  filterState: 'All comments'
};

TextEditorAnnotationComment.propTypes = {
  activeAnnotation: PropTypes.string.isRequired,
  allContentIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  yPos: PropTypes.number.isRequired,
  hide: PropTypes.func,
  storyId: PropTypes.number.isRequired,
  filterState: PropTypes.string
};



// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/TextEditorAnnotationComment.jsx